import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Lock as LockIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { API_URL, createAxiosInstance } from "../../../api";
// @ts-ignore - SVG import
import successIcon from "../../../assets/success-icon.svg";
// @ts-ignore - PNG import
import stripeLogo from "../../../assets/stripelogo.png";
// @ts-ignore - JSX import
import PricingComparison from "../../Pricing/PricingComparison";
// @ts-ignore - JSX import
import UpgradePaymentForm from "../../Pricing/UpgradePaymentForm";

interface TrialStatus {
  onTrial: boolean;
  trialEndDate: string;
  daysRemaining: number;
  isExpired: boolean;
}

export default function Subscription({ onDirtyChange, onValidChange, onSave }: any) {
  const stripe = useStripe();
  const elements = useElements();
  const userId = useSelector((state: any) => state.main.userId);
  
  const [paid, setPaid] = useState(false);
  const [paidStatus, setPaidStatus] = useState("false");
  const [statusLoading, setStatusLoading] = useState(true);
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  // No tier is selected by default — Premium Yearly carries a visual "Best
  // Value" recommendation glow but the user still has to click something to
  // make a choice. The Pricing page can pre-select Monthly/Yearly via the
  // `?plan=` query string.
  const [searchParams] = useSearchParams();
  const initialPlan = (() => {
    const requested = searchParams.get("plan");
    if (requested === "monthly" || requested === "yearly" || requested === "basic") {
      return requested;
    }
    return "";
  })();
  const [subscriptionType, setSubscriptionType] = useState(initialPlan);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [cancelledInfo, setCancelledInfo] = useState<{ cancelAtPeriodEnd: boolean; activeUntil: string } | null>(null);
  const [isPaymentExempt, setIsPaymentExempt] = useState(false);
  // When hasPaid returns { paid: "Paid", planTier: "basic" } we want to show
  // the Upgrade-to-Premium widget instead of the generic "Payment Plan is
  // Active" success card.
  const [activePlanTier, setActivePlanTier] = useState<string | null>(null);
  // Cadence chosen by clicking a card inside the inline Upgrade widget on the
  // Basic branch. Drives the visual highlight + the rendering of the inline
  // Stripe payment form. Pre-populated from `?plan=monthly|yearly` when the
  // user arrives from the Pricing page / How It Works.
  const [upgradeCadence, setUpgradeCadence] = useState<"monthly" | "yearly" | "">(
    initialPlan === "monthly" || initialPlan === "yearly" ? initialPlan : ""
  );

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!userId) {
        console.log("User ID is undefined or invalid. Skipping payment check.");
        return;
      }

      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );

        const paidStatus = response.data?.paid;
        const isPaid = paidStatus === "Paid" || paidStatus === true;

        if (response.data?.paymentExempt) {
          setIsPaymentExempt(true);
        }
        if (response.data?.planTier) {
          setActivePlanTier(response.data.planTier);
        }
        // If cancel_at_period_end is set the plan is winding down. Surface a
        // notice and route the user to the 3-card chooser regardless of
        // whether the prepaid window is still active — per the V2 rule that
        // any cancelled/expired plan shows the full chooser, not the active
        // success card.
        if (response.data?.cancelAtPeriodEnd) {
          setCancelledInfo({
            cancelAtPeriodEnd: true,
            activeUntil: response.data.activeUntil,
          });
          setPaid(false);
          setPaidStatus("false");
        } else if (response.data && isPaid) {
          setPaid(true);
          setPaidStatus(response.data.paid);
        } else {
          setPaid(false);
          setPaidStatus("false");
        }

        // Check if user is on trial
        if (response.data?.onTrial) {
          try {
            const trialResponse = await axiosInstance.get(
              `${API_URL}/api/billings/trial/status/${userId}`
            );
            if (trialResponse.data?.success && trialResponse.data?.hasTrial) {
              setTrialStatus({
                onTrial: true,
                trialEndDate: trialResponse.data.trialEndDate,
                daysRemaining: trialResponse.data.daysRemaining,
                isExpired: trialResponse.data.isExpired,
              });
              // If on active trial, mark as paid
              if (!trialResponse.data.isExpired) {
                setPaid(true);
                setPaidStatus("Paid");
              }
            }
          } catch (trialError) {
            console.error("Error fetching trial status:", trialError);
          }
        }
      } catch (error: any) {
        console.error(
          "Error checking payment status:",
          error.response?.data?.message || error.message
        );
        setPaidStatus("false");
      } finally {
        setStatusLoading(false);
      }
    };

    checkPaymentStatus();
    onDirtyChange?.(false);
  }, [userId]);

  const cardElementStyles = {
    base: {
      backgroundColor: "light-grey",
      fontSize: "18px",
      color: "#32325d",
      "::placeholder": {
        color: "#aab7c4",
      },
      border: "1px solid #ccc",
      borderRadius: "4px",
      padding: "10px",
      marginBottom: "16px",
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
    focus: {
      borderColor: "#712d93",
    },
  };

  const handlePayment = async () => {
    if (!stripe || !elements) {
      Swal.fire({
        icon: "error",
        title: "Payment System Loading",
        text: "Please wait while the payment system initializes",
      });
      return;
    }
    setPaymentLoading(true);

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: "Card element is not available.",
      });
      setPaymentLoading(false);
      return;
    }

    try {
      const { error, token } = await stripe.createToken(cardNumberElement);

      if (error) {
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: error.message,
        });
        setPaymentLoading(false);
        return;
      }

      const axiosInstance = createAxiosInstance();

      if (!firstName.trim() || !lastName.trim()) {
        Swal.fire({
          icon: "error",
          title: "Missing Information",
          text: "Please enter both first and last name",
        });
        setPaymentLoading(false);
        return;
      }

      const response = await axiosInstance.post(`${API_URL}/api/billings/stripe`, {
        token: token.id,
        subscriptionType,
        name: `${firstName} ${lastName}`,
        userId,
      });

      // Handle 3D Secure authentication if required
      if (response.data?.requiresAction && response.data?.clientSecret) {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          response.data.clientSecret
        );

        if (confirmError || paymentIntent?.status !== "succeeded") {
          // Clean up the incomplete subscription in Stripe
          if (response.data?.subscriptionId) {
            try {
              await axiosInstance.post(`${API_URL}/api/billings/cancel-incomplete`, {
                subscriptionId: response.data.subscriptionId,
              });
            } catch (cleanupErr) {
              console.error("Failed to clean up incomplete subscription:", cleanupErr);
            }
          }
          throw new Error(confirmError?.message || "Payment authentication was not completed successfully");
        }

        console.log("3D Secure authentication completed successfully");
      }

      if (response.data && (response.data.success || response.data.requiresAction)) {
        Swal.fire({
          imageUrl: successIcon,
          imageHeight: "100px",
          title: "Payment Successful",
          text: "Your subscription has been activated!",
          confirmButtonColor: "#712d94",
          customClass: {
            icon: "no-before-icon",
          },
        }).then(() => {
          setPaid(true);
          setPaidStatus("Paid");
          onDirtyChange?.(false);
          onSave?.();
          // Dispatch event to notify other components
          window.dispatchEvent(new Event('paymentCompleted'));
        });
      } else {
        throw new Error(response.data.message || "Payment failed");
      }
    } catch (error: any) {
      console.error("Payment processing error:", error);
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text:
          error.response?.data?.message ||
          "There was an issue processing your payment.",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    // Notify parent of validation status. The step is valid when either the
    // tutor already has an active subscription / trial / exemption, OR they've
    // explicitly chosen the free Basic plan in this step.
    const isValid = paidStatus !== "false" || subscriptionType === "basic";
    onValidChange?.(isValid);
  }, [paidStatus, subscriptionType, onValidChange]);

  (Subscription as any).save = async () => {
    // If user already has active subscription, there's nothing to save
    if (paidStatus !== "false") {
      onDirtyChange?.(false);
      return;
    }
    // If the tutor explicitly chose the free Basic plan, persist that choice so
    // the Submit step recognises the Plan step as complete.
    if (subscriptionType === "basic" && userId) {
      try {
        const axiosInstance = createAxiosInstance();
        await axiosInstance.post(`${API_URL}/api/billings/select-basic/${userId}`);
        setPaidStatus("Paid");
        window.dispatchEvent(new Event("paymentCompleted"));
      } catch (e) {
        console.error("Failed to record Basic plan selection:", e);
      }
    }
    onDirtyChange?.(false);
  };

  const monthlyFeatures = [
    "Premium tutor profile",
    "Priority visibility",
    "2 course adverts per year",
  ];
  const yearlyFeatures = [
    "Save 27% vs monthly",
    "2 course adverts per year",
    "Best for serious tutors",
  ];

  if (statusLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h2"
        sx={{
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: '#712d94 !important' }}>
          Choose Your Payment Plan
        </Box>
        

      </Typography>
      <Box 
        sx={{ 
          '&': {
            marginBottom: '1.5rem !important'
          }
        }}
      >
        <Typography
          sx={{
            color: "#712d94",
            fontWeight: 600,
            fontSize: { xs: "1.125rem", md: "1.25rem" },
            lineHeight: 1.4,
          }}
        >
          Promote your tutoring services and attract more enquiries.
        </Typography>
      </Box>

      {trialStatus?.onTrial && !trialStatus?.isExpired ? (
        <Box sx={{ 
          p: 3, 
          backgroundColor: '#d1ecf1', 
          border: '1px solid #bee5eb',
          borderRadius: 2,
          mb: 4,
        }}>
          <Typography variant="h2" sx={{ mb: 1, color: '#0c5460' }}>
            You're on a Free Trial
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#0c5460' }}>
            Your free trial is active until {new Date(trialStatus.trialEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
            You can skip this step for now and subscribe later.
          </Typography>
        </Box>
      ) : paidStatus === "false" ? (
        <>
          {cancelledInfo && (() => {
            // Pick "cancelled" (winding down) vs "expired" (past end date)
            // based on the actual end date, not on `paid` — so the banner
            // never says "will end" about a date that's already passed.
            const endMs = cancelledInfo.activeUntil
              ? new Date(cancelledInfo.activeUntil).getTime()
              : null;
            const isAfterEnd = endMs ? Date.now() >= endMs : !paid;
            const endDateText = endMs
              ? new Date(endMs).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
              : null;
            const accent = isAfterEnd ? "#d32f2f" : "#f59e0b";
            const bg = isAfterEnd ? "rgba(211, 47, 47, 0.05)" : "rgba(245, 158, 11, 0.05)";
            return (
              <Card
                elevation={0}
                sx={{
                  border: `2px solid ${accent}`,
                  borderRadius: 2,
                  backgroundColor: bg,
                  mb: 4,
                }}
              >
                <CardContent sx={{ p: 4, textAlign: "center" }}>
                  <Typography sx={{ mb: 2, fontWeight: 700, color: accent, fontSize: "1.25rem", lineHeight: 1.3, textAlign: "center" }}>
                    {isAfterEnd ? "Plan Expired" : "Payment Plan Cancelled"}
                  </Typography>
                  <Typography sx={{ mb: 2, color: "#6b7280", textAlign: "center", fontSize: "1rem", lineHeight: 1.5 }}>
                    {isAfterEnd
                      ? (endDateText
                          ? `Your payment plan ended on ${endDateText} and your profile is no longer visible to students.`
                          : "Your payment plan has expired and your profile is no longer visible to students.")
                      : (endDateText
                          ? `Your plan has been cancelled and will end on ${endDateText}.`
                          : "Your plan has been cancelled.")}
                  </Typography>
                  <Typography sx={{ color: "#9ca3af", fontStyle: "italic", textAlign: "center", fontSize: "0.875rem", lineHeight: 1.5 }}>
                    {isAfterEnd
                      ? "Stay on Basic (free) or pick a Premium plan below to bring your enhanced profile back."
                      : "Stay on Basic (free) or pick a Premium plan below to keep your enhanced profile."}
                  </Typography>
                </CardContent>
              </Card>
            );
          })()}
          <Box sx={{ mb: 4, mt: 1 }}>
            <PricingComparison
              hideHeading
              selectedTier={subscriptionType}
              basicCtaLabel={subscriptionType === "basic" ? "Selected" : "Choose Basic"}
              monthlyCtaLabel={subscriptionType === "monthly" ? "Selected" : "Choose monthly"}
              yearlyCtaLabel={subscriptionType === "yearly" ? "Selected" : "Choose yearly"}
              onSelectBasic={() => {
                setSubscriptionType("basic");
                onDirtyChange?.(true);
              }}
              onSelectMonthly={() => {
                setSubscriptionType("monthly");
                onDirtyChange?.(true);
              }}
              onSelectYearly={() => {
                setSubscriptionType("yearly");
                onDirtyChange?.(true);
              }}
            />
          </Box>

          {subscriptionType === "basic" ? (
            <Card
              elevation={0}
              sx={{
                border: "1px solid #e9e9ec",
                borderRadius: 2,
                mb: 3,
                backgroundColor: "rgba(113, 45, 148, 0.04)",
              }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#712d94", mb: 1 }}>
                  You're all set with the Basic plan
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", maxWidth: "640px", mx: "auto" }}>
                  No payment needed. You can upgrade to Premium any time from your dashboard
                  to unlock website, phone, social links, the Featured badge, priority visibility,
                  and 2 free course adverts per year.
                </Typography>
              </CardContent>
            </Card>
          ) : (
          <>
          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 4, color: "#6b7280", maxWidth: "800px", mx: "auto" }}
          >
            We charge a small subscription fee to cover website hosting, secure payments, and
            continuous improvements - so we can connect you with more students and keep
            everything running smoothly.
          </Typography>

          <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
          mb: 3,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 3,
            }}
          >
            <LockIcon sx={{ color: "#712d94", fontSize: "20px" }} />
            <Typography variant="h5" align="center" sx={{ fontWeight: 600 }}>
              Payment Details (Secure via Stripe)
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  onDirtyChange?.(true);
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  onDirtyChange?.(true);
                }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <CardNumberElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <CardExpiryElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <CardCvcElement options={{ style: cardElementStyles }} />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={handlePayment}
              loading={paymentLoading}
              disabled={!stripe || paymentLoading || !firstName || !lastName}
              sx={{
                minWidth: "200px",
                height: "48px",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Pay Now
            </LoadingButton>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <img
              src={stripeLogo}
              alt="Powered by Stripe"
              style={{ maxWidth: "150px", height: "auto" }}
            />
          </Box>
        </CardContent>
      </Card>
          </>
          )}

          <Typography
            variant="h2"
            align="center"
            sx={{ mt: 3, color: "#1a1a1a", fontStyle: "italic", fontWeight: 700 }}
          >
            Join{" "}
            <Box component="span" sx={{ color: "#712d94" }}>
              400+ tutors
            </Box>{" "}
            already building their profiles with us.
          </Typography>
        </>
      ) : activePlanTier === "basic" ? (
        // User is on the free Basic plan — show the 3-card chooser so they can
        // stay on Basic or pick a Premium cadence. Picking a cadence reveals
        // the inline payment form below.
        <Box sx={{ my: 3 }}>
          <Card
            elevation={0}
            sx={{
              border: "2px solid #712d94",
              borderRadius: 2,
              backgroundColor: "rgba(113, 45, 148, 0.05)",
              mb: 4,
            }}
          >
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Typography sx={{ mb: 2, fontWeight: 700, color: "#712d94", fontSize: "1.25rem", lineHeight: 1.3, textAlign: "center" }}>
                You're on the Basic plan
              </Typography>
              <Typography sx={{ color: "#6b7280", fontSize: "1rem", lineHeight: 1.5, textAlign: "center" }}>
                No payment needed to keep your profile listed. Upgrade any time to unlock
                website, phone, social links, the Featured badge, priority visibility, and
                2 free course adverts per year.
              </Typography>
            </CardContent>
          </Card>
          <PricingComparison
            hideHeading
            selectedTier={upgradeCadence || "basic"}
            basicCtaLabel="Current free plan"
            monthlyCtaLabel={upgradeCadence === "monthly" ? "Selected" : "Choose monthly"}
            yearlyCtaLabel={upgradeCadence === "yearly" ? "Selected" : "Choose yearly"}
            onSelectMonthly={() => {
              setUpgradeCadence("monthly");
              onDirtyChange?.(true);
            }}
            onSelectYearly={() => {
              setUpgradeCadence("yearly");
              onDirtyChange?.(true);
            }}
          />
          {upgradeCadence && (
            <UpgradePaymentForm
              cadence={upgradeCadence}
              userId={userId}
              onSuccess={() => {
                setPaid(true);
                setPaidStatus("Paid");
                setActivePlanTier("premium");
                setUpgradeCadence("");
                onDirtyChange?.(false);
                onSave?.();
              }}
            />
          )}
        </Box>
      ) : (
        <>
        <Card
          elevation={0}
          sx={{
            border: "2px solid #24a148",
            borderRadius: 2,
            backgroundColor: "rgba(36, 161, 72, 0.05)",
            my: 3,
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "rgba(36, 161, 72, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckIcon sx={{ fontSize: 48, color: "#24a148" }} />
              </Box>
            </Box>
            <Typography variant="h2" sx={{ mb: 4, fontWeight: 700, color: "#24a148" }}>
              {isPaymentExempt ? "Free Account" : "Payment Plan is Active"}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: "#6b7280", textAlign: "center" }}>
              {isPaymentExempt
                ? "Your account has been granted free access. No subscription payment is required."
                : "Your payment plan is active and you're all set! You can continue to the next step."}
            </Typography>
            <Typography variant="body2" sx={{ color: "#9ca3af", fontStyle: "italic", textAlign: "center" }}>
              {isPaymentExempt
                ? "You can create and manage your profile without a subscription."
                : "Thank you for supporting our platform!"}
            </Typography>
          </CardContent>
        </Card>

        {/* Optional upgrade for exempt users — they don't have to pay, but
            they can choose Premium if they want the extra features. */}
        {isPaymentExempt && (
          <Box sx={{ mt: 4 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: "1.125rem",
                mb: 0.5,
                textAlign: "center",
              }}
            >
              Want to upgrade to Premium?
            </Typography>
            <Typography sx={{ color: "#6b7280", fontSize: "0.9rem", mb: 3, textAlign: "center" }}>
              You don't have to — your account stays free. Pick a Premium plan
              below if you'd like the enhanced profile features.
            </Typography>
            <PricingComparison
              hideHeading
              hideBasic
              selectedTier={upgradeCadence || undefined}
              monthlyCtaLabel={upgradeCadence === "monthly" ? "Selected" : "Choose monthly"}
              yearlyCtaLabel={upgradeCadence === "yearly" ? "Selected" : "Choose yearly"}
              onSelectMonthly={() => setUpgradeCadence("monthly")}
              onSelectYearly={() => setUpgradeCadence("yearly")}
            />
            {upgradeCadence && (
              <UpgradePaymentForm
                cadence={upgradeCadence}
                userId={userId}
                onSuccess={() => {
                  setPaid(true);
                  setPaidStatus("Paid");
                  setActivePlanTier("premium");
                  setIsPaymentExempt(false);
                  setUpgradeCadence("");
                  onDirtyChange?.(false);
                  onSave?.();
                }}
              />
            )}
          </Box>
        )}
        </>
      )}
    </Box>
  );
}
